// Normalizes the cancel reason of an order into a short category (tasks#97).
// Sources, as written by the backends:
// - `cancelReason`, customer cancel (ordit OrderController):
//   'Cancelled on request of user/app, useragent: <UA>' (HU: 'Felhasználó/app kérésére
//   lemondva, user-agent: <UA>')
// - `cancelReason`, payment failure (ordit PaymentHandler): ' <paymentMethod>:<gateway message>'
//   (bare gateway messages without the prefix on rows from 2022)
// - `cancelReason`, Deliverect: ' Deliverect: <reason>'
// - `adminCancelReasonType` + `adminCancelReason`: the admin cancel modal
// `Ordering::addCancelReason()` appends without a separator, so a mixed customer + payment text
// falls back to 'other' with the full text as detail.

export const CANCELLED_STATUSES = ['cancelled', 'cancelled-after-approval']

const ADMIN_TYPES = [
    'not_confirmed',
    'stock_issue',
    'technical_issue',
    'restaurant_cancelled',
    'customer_cancelled',
    'company_cancelled',
    'other',
]

// ordit PaymentMethodEnum names
const PAYMENT_METHODS =
    'barion|cash|elizabeth|sodexo-pass|ticket-restaurant|otp|stripe|knh'
const PAYMENT_PREFIX = new RegExp(`^(${PAYMENT_METHODS}):\\s*([\\s\\S]*)$`, 'i')
const PAYMENT_ANYWHERE = new RegExp(`(^|\\s)(${PAYMENT_METHODS}):`, 'i')

const DELIVERECT_PREFIX = /^deliverect:/i
const CUSTOMER_PREFIXES = [
    'cancelled on request of user/app',
    'felhasználó/app kérésére lemondva',
]
const USER_AGENT = /(, )?(useragent|user-agent):\s*([\s\S]*)$/i

// Checked in this order; the first match wins.
const PAYMENT_DETAILS = [
    ['insufficient_funds', /insufficient funds/i],
    ['too_many_attempts', /repeated attempts|maximum number of declines/i],
    ['authentication', /authentication|action_required/i],
    ['declined', /declined/i],
    ['expired', /expired/i],
    ['wrong_number', /card number/i],
    ['wrong_cvc', /security code/i],
    ['invalid_account', /invalid account/i],
    ['not_supported', /does not support/i],
    ['attempt_failed', /latest payment attempt/i],
    ['processing_error', /error occurred while processing/i],
]

function paymentDetailKey(message) {
    const match = PAYMENT_DETAILS.find(([, pattern]) => pattern.test(message))
    return match ? match[0] : null
}

function stripUserAgent(text) {
    return text.replace(USER_AGENT, '').trim()
}

function customerChannel(userAgent) {
    if (/^Ordit\//.test(userAgent)) {
        return 'ios'
    }
    if (/^okhttp\//i.test(userAgent)) {
        return 'android'
    }
    if (/^Mozilla\//.test(userAgent)) {
        return 'web'
    }
    return null
}

function classifySystemReason(text) {
    const lower = text.toLowerCase()
    const userAgent = text.match(USER_AGENT)
    const isCustomer =
        CUSTOMER_PREFIXES.some((prefix) => lower.startsWith(prefix)) ||
        !!userAgent

    // The payment text may follow the user agent, so check (and show) the full text.
    if (isCustomer && PAYMENT_ANYWHERE.test(text)) {
        return { group: 'other', key: 'other', detail: text }
    }

    if (DELIVERECT_PREFIX.test(text)) {
        return { group: 'deliverect', key: 'deliverect', detail: text }
    }

    if (isCustomer) {
        const channel = customerChannel(userAgent ? userAgent[3].trim() : '')
        return {
            group: 'customer',
            key: channel === 'web' ? 'web' : channel ? 'app' : 'unknown',
            channel,
            detail: null,
        }
    }

    const payment = text.match(PAYMENT_PREFIX)
    if (payment) {
        const message = payment[2].trim()
        return {
            group: 'payment',
            key: paymentDetailKey(message) || 'failed',
            method: payment[1].toLowerCase(),
            detail: message || null,
        }
    }

    const bareGatewayKey = paymentDetailKey(text)
    if (bareGatewayKey) {
        return {
            group: 'payment',
            key: bareGatewayKey,
            method: null,
            detail: text,
        }
    }

    return { group: 'other', key: 'other', detail: stripUserAgent(text) }
}

/**
 * @returns {null|{group: string, key: string, channel?: string|null, method?: string|null, detail: string|null}}
 */
export function classifyCancelReason(order) {
    if (!CANCELLED_STATUSES.includes(order?.status)) {
        return null
    }

    const text =
        typeof order.cancelReason === 'string' ? order.cancelReason.trim() : ''
    if (text) {
        return classifySystemReason(text)
    }

    const type = order.adminCancelReasonType
    if (type) {
        return {
            group: 'admin',
            key: ADMIN_TYPES.includes(type) ? type : 'other',
            detail: order.adminCancelReason?.trim() || null,
        }
    }

    return null
}
